import "@preact/signals";
import * as preact from "preact";

import * as genericModals from "genericModals";
import { simpleMarkupToJsx } from "simpleMarkupJsx";
import BookStore from "stores/BookStore";
import { getPrivacyValue, getValidBooks, ShelfData } from "stores/ShelvesStore";
import stores from "stores/stores";

interface BookCollectionLike {
  get(id: string): BookStore | undefined;
}

interface AppRouterLike {
  navigate(path: string, options?: { trigger?: boolean }): void;
}

type EditMode = "name" | "description" | null;

type PrivacyValue = ReturnType<typeof getPrivacyValue>;

const privacyUpdates: Record<PrivacyValue, Partial<ShelfData>> = {
  private: { public: false, hide: false },
  linkOnly: { public: true, hide: true },
  public: { public: true, hide: false },
};

const confirmUnshare = () =>
  new Promise<boolean>((resolve) =>
    genericModals.confirm({
      title: "Un-Share shelf?",
      message:
        "Other users will no longer be able to view this shelf. Are you sure you want to " +
        "un-share it?",
      callback: (response: string) => resolve(response === "ok"),
    })
  );

interface Props {
  shelfID?: string;
  books: BookCollectionLike;
  appRouter: AppRouterLike;
}

interface State {
  editMode: EditMode;
  /** The Access value being applied, shown until the shelf is patched */
  pendingPrivacyValue: PrivacyValue | null;
}

class ShelfEditView extends preact.Component<Props, State> {
  state: State = { editMode: null, pendingPrivacyValue: null };

  nameInputRef: preact.RefObject<HTMLInputElement> = preact.createRef();
  descriptionTextAreaRef: preact.RefObject<HTMLTextAreaElement> =
    preact.createRef();

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.editMode !== this.state.editMode) {
      const shelf = this.getShelf();
      if (this.state.editMode === "name" && this.nameInputRef.current) {
        this.nameInputRef.current.value = shelf?.name ?? "";
        this.nameInputRef.current.focus();
      } else if (
        this.state.editMode === "description" &&
        this.descriptionTextAreaRef.current
      ) {
        this.descriptionTextAreaRef.current.value = shelf?.description ?? "";
        this.descriptionTextAreaRef.current.focus();
      }
    }
  }

  getShelf(): ShelfData | undefined {
    const shelfID = this.props.shelfID;
    if (!shelfID) return undefined;
    const shelf = stores.shelves.get(shelfID);
    if (!shelf || shelf._id === "unshelved") return undefined;
    return shelf;
  }

  cancelEdit = () => {
    this.setState({ editMode: null });
  };

  editShelfName = () => {
    this.setState({ editMode: "name" });
  };

  saveShelfName = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();

    const shelf = this.getShelf();
    if (!shelf) return;

    const name = this.nameInputRef.current?.value ?? "";
    if (name.trim().length === 0) {
      window.alert("Please give the shelf a name first");
      return;
    }
    if (shelf.name === name) {
      this.setState({ editMode: null });
    } else {
      stores.shelves.patchShelf(shelf._id, { name });
      this.setState({ editMode: null });
    }
  };

  editShelfDescription = () => {
    this.setState({ editMode: "description" });
  };

  saveShelfDescription = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();

    const shelf = this.getShelf();
    if (!shelf) return;

    const description = this.descriptionTextAreaRef.current?.value ?? "";

    if (shelf.description === description) {
      this.setState({ editMode: null });
    } else {
      stores.shelves.patchShelf(shelf._id, { description });
      this.setState({ editMode: null });
    }
  };

  inputKeyup = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      this.cancelEdit();
    }
  };

  clickDescriptionLink = (event: Event) => {
    event.stopPropagation();
  };

  changePrivacySettings = async (event: Event) => {
    const shelf = this.getShelf();
    if (!shelf) return;

    const newValue = (event.target as HTMLSelectElement).value as PrivacyValue;

    const privateTexts = getValidBooks(shelf).filter((shelfBook) => {
      const book = this.props.books.get(shelfBook.bookID);
      return book && !book.book.value.public;
    });
    const unlistedPublicTexts = getValidBooks(shelf).filter((shelfBook) => {
      const book = this.props.books.get(shelfBook.bookID);
      return book && book.book.value.public && book.book.value.hide;
    });

    // Each text's update below re-renders this view, while the shelf still has
    // its old value, so show the chosen value until the shelf is patched
    this.setState({ pendingPrivacyValue: newValue });
    try {
      switch (newValue) {
        case "private":
          if (!(await confirmUnshare())) return;
          break;

        case "linkOnly":
          if (
            privateTexts.length > 0 &&
            !confirm(
              "This will make " +
                privateTexts.length +
                " currently private text" +
                (privateTexts.length === 1 ? "" : "s") +
                " accessible to anyone with the link. Are you sure?"
            )
          ) {
            return;
          }
          for (const book of privateTexts) {
            await this.props.books.get(book.bookID)?.addTag("shared", {
              hide: true,
            });
          }
          break;

        case "public":
          if (
            privateTexts.length > 0 &&
            !confirm(
              "This will make " +
                privateTexts.length +
                " currently private text" +
                (privateTexts.length === 1 ? "" : "s") +
                " public. Are you sure?"
            )
          ) {
            return;
          }
          if (
            unlistedPublicTexts.length > 0 &&
            !confirm(
              "This will make " +
                unlistedPublicTexts.length +
                " text" +
                (unlistedPublicTexts.length === 1 ? "" : "s") +
                " currently only accessible via the link" +
                " accessible to everyone. Are you sure?"
            )
          ) {
            return;
          }
          for (const book of privateTexts) {
            await this.props.books.get(book.bookID)?.addTag("shared");
          }
          for (const book of unlistedPublicTexts) {
            await this.props.books.get(book.bookID)?.patchBook({ hide: false });
          }
          break;

        default:
          throw Error("Invalid privacy setting: " + newValue);
      }
      await stores.shelves.patchShelf(shelf._id, privacyUpdates[newValue]);
    } finally {
      this.setState({ pendingPrivacyValue: null });
    }
  };

  deleteShelf = () => {
    const shelf = this.getShelf();
    if (!shelf) return;

    genericModals.confirm({
      title: "Delete Shelf",
      warning: true,
      message:
        "Are you sure you want to delete this shelf?\n\n" +
        "This cannot be undone!\n\n" +
        "(None of the actual texts will be deleted)",
      callback: async (response: string) => {
        if (response === "ok") {
          await stores.shelves.patchShelf(shelf._id, {
            deleted: true,
          });
          stores.shelves.selectedShelfID.value = null;
          this.props.appRouter.navigate("/myTexts/queue", {
            trigger: true,
          });
        }
      },
    });
  };

  render() {
    const shelf = this.getShelf();
    if (!shelf) return null;

    const editingName = this.state.editMode === "name";
    const editingDescription = this.state.editMode === "description";
    const privacyValue =
      this.state.pendingPrivacyValue ?? getPrivacyValue(shelf);

    return (
      <div>
        <h2
          class="shelfName view editableContainer"
          style={{ display: editingName ? "none" : undefined }}
          onClick={this.editShelfName}
        >
          {shelf.name ? shelf.name : "Enter shelf name here..."}
          <span class="editableHint">
            <i class="icon-pencil"></i>{" "}
            <small class="noMobile">edit shelf name</small>
          </span>
        </h2>

        <form
          class="shelfName edit"
          style={{ display: editingName ? "block" : "none" }}
          onSubmit={this.saveShelfName}
        >
          <table>
            <tr>
              <td>
                <input
                  type="text"
                  class="shelfNameInput"
                  ref={this.nameInputRef}
                  onKeyUp={this.inputKeyup}
                />
              </td>
              <td>
                <button
                  class="saveShelfName prominent"
                  onClick={this.saveShelfName}
                >
                  Save
                </button>
              </td>
            </tr>
          </table>
        </form>

        <p
          class="shelfDescription view editableContainer"
          style={{ display: editingDescription ? "none" : undefined }}
          onClick={this.editShelfDescription}
        >
          {shelf.description ? (
            <span
              class="shelfDescriptionContent"
              onClick={(event) => {
                const target = event.target as HTMLElement;
                if (target.tagName === "A" && target.getAttribute("href")) {
                  event.stopPropagation();
                }
              }}
            >
              {simpleMarkupToJsx(shelf.description)}
            </span>
          ) : (
            "Add shelf description here..."
          )}
          <span class="editableHint">
            <i class="icon-pencil"></i>{" "}
            <small class="noMobile">edit shelf description</small>
          </span>
        </p>

        <form
          class="shelfDescription edit"
          style={{ display: editingDescription ? "block" : "none" }}
          onSubmit={this.saveShelfDescription}
        >
          <table>
            <tr>
              <td>
                <textarea
                  rows={5}
                  class="shelfDescriptionTextArea"
                  ref={this.descriptionTextAreaRef}
                  onKeyUp={this.inputKeyup}
                />
              </td>
              <td>
                <button
                  class="saveShelfDescription prominent"
                  onClick={this.saveShelfDescription}
                >
                  Save
                </button>
              </td>
            </tr>
          </table>
        </form>

        <button
          class="deleteShelf"
          style={{ float: "right" }}
          onClick={this.deleteShelf}
        >
          Delete Shelf
        </button>

        <p>
          Access:{" "}
          <select
            name="privacySettings"
            value={privacyValue}
            onChange={this.changePrivacySettings}
          >
            <option value="private">Only you</option>
            <option value="linkOnly">Only those with the link</option>
            <option value="public">Everyone</option>
          </select>
        </p>
        <span
          class="shareLink"
          style={{ display: privacyValue === "private" ? "none" : undefined }}
        >
          (<a href={"/shelf/" + shelf._id}>Shareable Link</a>)
        </span>
      </div>
    );
  }
}

export default ShelfEditView;
